import { Component } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import throttle from "lodash/throttle"
import loadMonaco from "shared/lib/load_monaco"
import parse from "html-react-parser"

import esLibDefs from "./es_lib_defs"
import { IFTTT_DARK_THEME, defineIftttDarkTheme } from "./ifttt_dark_theme"
import momentLibSource from "moment/ts3.1-typings/moment.d.ts?raw"
import momentTimezoneLibSource from "moment-timezone/index.d.ts?raw"

import FormatCodeIcon from "images/icons/icon-paragraph.svg?raw"

// The moment-timezone typings use generics, which monaco-editor(?) doesn't
// seem to support. The method using generics is obscure, anyway (zonesForCountry).
const modifiedMomentTimezoneLibSource = momentTimezoneLibSource.replace(/^.*<\w+ extends .*$/gm, "")

const contextFilename = "context.d.ts"
const extraLibsForEditor = {
  ...esLibDefs,
  "moment.d.ts": momentLibSource,
  "moment-timezone.d.ts": modifiedMomentTimezoneLibSource,
}

/**
 * This is a React wrapper for the Monaco editor. It takes up the full width of
 * whatever container it's placed in. Note that this doesn't directly depend on
 * the Monaco module because Monaco wants to load itself dynamically at
 * runtime, which is probably a good thing for our bundle size anyway.
 *
 * This is an uncontrolled component since I'm not sure how being controlled
 * would interact with Monaco's autocomplete, etc.
 *
 * Mostly borrowed from:
 * - https://github.com/superRaytin/react-monaco-editor
 * - https://github.com/Microsoft/monaco-editor/blob/master/website/playground
 */
export default class MonacoEditor extends Component {
  constructor(props) {
    super(props)
    this.initMonaco = this.initMonaco.bind(this)

    this.updateEditorLayout = throttle(this.updateEditorLayout.bind(this), 250)
  }

  // Never re-render after the initial render; in order to minimize weird
  // interactions with the editor, we want to just render once and handle any
  // further updates manually.
  shouldComponentUpdate() {
    return false
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    this.updateTypescriptContext(newProps.typescriptContext)
  }

  componentDidMount() {
    window.addEventListener("resize", this.updateEditorLayout)

    // The container also resizes without the window: a sibling panel growing,
    // the phone drawer opening.
    this._resizeObserver = new ResizeObserver(this.updateEditorLayout)
    this._resizeObserver.observe(this.editorRef)

    loadMonaco().then(this.initMonaco)
  }

  componentWillUnmount() {
    this._unmounted = true
    window.removeEventListener("resize", this.updateEditorLayout)
    this._resizeObserver.disconnect()
    // Deferred layouts must not reach a disposed editor.
    this.updateEditorLayout.cancel()
    clearTimeout(this._firefoxLayoutTimer)

    if (this._extraLibHandle) {
      this._extraLibHandle.dispose()
    }

    if (this._editor) {
      this._editor.dispose()
      this._editor = null
    }
  }

  initMonaco() {
    // Monaco loads asynchronously; the component may be gone by the time it lands.
    if (this._unmounted) return
    if (this.props.theme === IFTTT_DARK_THEME) defineIftttDarkTheme(window.monaco)

    this._editor = window.monaco.editor.create(this.editorRef, {
      value: this.props.defaultValue,
      language: this.props.language,
      lineNumbersMinChars: 3,
      overviewRulerLanes: 0,
      scrollBeyondLastLine: false,
      wrappingColumn: 0,
      wrappingIndent: "indent",
      tabCompletion: true,
      folding: false,
      roundedSelection: false,
      contextmenu: false,
      renderLineHighlight: false,
      iconsInSuggestions: false,
      fontFamily: '"Menlo", "SFMono-Regular", "Consolas", "Liberation Mono", monospace', // Matches the `$code` var in 'foundation/_typography.scss'
      codeLens: false,

      fontSize: 14,
      suggestLineHeight: 18 + 2 * 8, // 8px of extra padding (cf `.suggest-widget` styles in CSS)
      theme: this.props.theme,
      ...this.props.editorOptions,
    })

    this._editor.getModel().updateOptions({
      tabSize: 2,
      insertSpaces: true,
      trimAutoWhitespace: true,
    })

    // Add our own ECMAScript definitions instead of using the default lib,
    // because the default one includes a bunch of Web APIs that aren't relevant.
    if (this.props.language === "typescript") {
      const tsDefaults = window.monaco.languages.typescript.typescriptDefaults
      const extraLibs = tsDefaults.getExtraLibs()

      for (const [filename, libSource] of Object.entries(extraLibsForEditor)) {
        if (!extraLibs[filename]) {
          tsDefaults.addExtraLib(libSource, filename)
        }
      }
    }

    if (this.props.typescriptContext) {
      this.updateTypescriptContext(this.props.typescriptContext)
    }

    this._editor.onDidChangeModelContent(event => {
      const value = this._editor.getValue()
      this.props.onChange(value, event)
    })
  }

  updateEditorLayout() {
    if (this._editor) {
      this._editor.layout()

      // FF/monaco editor has a delay to identify the correct width after resize. Re-run after 300ms for FF users.
      if (this._editor.getView().domNode.classList.contains("ff")) {
        this._firefoxLayoutTimer = setTimeout(() => this._editor?.layout(), 300)
      }
    }
  }

  updateTypescriptContext(newContext) {
    if (!this._editor || this._typescriptContext === newContext) {
      return
    }

    const tsDefaults = window.monaco.languages.typescript.typescriptDefaults

    if (this._extraLibHandle) {
      this._extraLibHandle.dispose()
    }
    this._typescriptContext = newContext
    this._extraLibHandle = tsDefaults.addExtraLib(newContext, contextFilename)

    // Match the runtime compiler's ES2024 target so modern syntax (optional
    // chaining, nullish coalescing, etc.) isn't flagged. The served Monaco
    // worker's enum only goes up to ES2022, so fall back to its newest target
    // (Latest/ESNext) — type resolution comes from the libs we register, not
    // from this target.
    const { ScriptTarget } = window.monaco.languages.typescript
    const target = ScriptTarget.ES2024 ?? ScriptTarget.Latest

    // Setting the options here has the extra purpose of forcing the editor to
    // refresh its diagnostics to reflect the new TypeScript context.
    tsDefaults.setCompilerOptions({
      ...tsDefaults.getCompilerOptions(),
      target,
      noImplicitAny: true,
      noLib: true,
      noResolve: true,
    })
  }

  onPrettifyClick = async () => {
    const prettier = await import("prettier/standalone")
    const babel = await import("prettier/parser-babel")

    this._editor.setValue(
      prettier.format(this._editor.getValue(), {
        parser: "babel",
        plugins: [babel],
        singleQuote: true,
      })
    )
  }

  render() {
    const { positionAbsolute, height, containerClassName } = this.props

    return (
      <div className="monaco-editor-wrapper-outer">
        <span className="monaco-editor-format-code">
          <span>{parse(FormatCodeIcon)}</span>
          <a className="link-caption" onClick={this.onPrettifyClick} data-open-in-current-window={true}>
            Format code
          </a>
        </span>
        <div
          className={classNames(containerClassName, {
            "monaco-editor-wrapper-inner": positionAbsolute,
          })}
          ref={r => (this.editorRef = r)}
          style={{ width: "100%", height }}
        />
        {positionAbsolute && (
          <div
            className="monaco-editor-wrapper-placeholder"
            style={{ height }}
          />
        )}
      </div>
    )
  }
}

MonacoEditor.propTypes = {
  positionAbsolute: PropTypes.bool,
  containerClassName: PropTypes.string,
  defaultValue: PropTypes.string,
  height: PropTypes.string,
  language: PropTypes.oneOf(["typescript"]), // others are available, see docs
  onChange: PropTypes.func,
  typescriptContext: PropTypes.string,
  theme: PropTypes.oneOf(["vs", IFTTT_DARK_THEME]),
  editorOptions: PropTypes.object,
}

MonacoEditor.defaultProps = {
  positionAbsolute: true,
  defaultValue: "",
  language: "typescript",
  height: 480,
  onChange: () => {},
  theme: "vs",
}
